import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BouncePnlReport({ dateRange = 'ytd', startDate, endDate }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rateEdit, setRateEdit] = useState('');
  const [savingRate, setSavingRate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let qs = `date_range=${dateRange}`;
      if (dateRange === 'custom' && startDate && endDate) qs += `&start_date=${startDate}&end_date=${endDate}`;
      const res = await axios.get(`${API}/reports/bounce-pnl?${qs}`, { headers: getAuthHeaders() });
      setData(res.data);
      setRateEdit(String(res.data.ach_return_cost_rate));
    } catch (e) {
      toast.error(`Failed to load bounce P&L: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, dateRange, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const saveRate = async () => {
    setSavingRate(true);
    try {
      await axios.put(`${API}/reports/bounce-pnl/settings`, { ach_return_cost: parseFloat(rateEdit) },
        { headers: getAuthHeaders() });
      toast.success('ACH return cost updated');
      await load();
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSavingRate(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>;
  if (!data) return null;
  const t = data.totals || {};

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Bounce Fees Charged', val: formatCurrency(t.fees_charged), color: 'text-slate-900' },
          { label: 'ACH Returns', val: t.returns_count, color: 'text-slate-900' },
          { label: 'Actual ACH Cost', val: formatCurrency(t.ach_cost), color: 'text-red-600' },
          { label: 'Margin', val: formatCurrency(t.margin), color: 'text-green-600' },
        ].map((c) => (
          <Card key={c.label}>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-500">{c.label}</CardTitle></CardHeader>
            <CardContent><div className={`text-2xl font-bold ${c.color}`}>{c.val}</div></CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-500">Cost per ACH return:</span>
        <Input className="w-24" value={rateEdit} onChange={(e) => setRateEdit(e.target.value)} />
        <Button size="sm" variant="outline" onClick={saveRate} disabled={savingRate}>
          {savingRate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Per Deal</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Deal</TableHead>
                <TableHead>Merchant</TableHead>
                <TableHead className="text-right">Fees Charged</TableHead>
                <TableHead className="text-right">Returns</TableHead>
                <TableHead className="text-right">ACH Cost</TableHead>
                <TableHead className="text-right">Margin</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.per_deal || []).map((d, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">#{d.deal_id}</TableCell>
                  <TableCell className="text-slate-600 max-w-[240px] truncate">{d.deal_name}</TableCell>
                  <TableCell className="text-right">{formatCurrency(d.fees_charged)}</TableCell>
                  <TableCell className="text-right">{d.returns_count}</TableCell>
                  <TableCell className="text-right text-red-600">{formatCurrency(d.ach_cost)}</TableCell>
                  <TableCell className={`text-right font-semibold ${d.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(d.margin)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
